/** Annotate → Scores: the shared score configs queues ask, Active or Archived. Archived ones are not
 *  offered to new queues; queues that ask them keep them, and their answers stay. */

import type { ReactNode } from "react";
import { Box } from "@/components/ui/box";
import { EmptyState, QueryState } from "@/components/ui/states";
import { ArchiveMenu } from "@/features/archive/archive-menu";
import { ArchiveEmpty, ArchiveTabs } from "@/features/archive/archived";
import { useArchiveTabs } from "@/features/archive/hooks";
import { formatRelative } from "@/lib/format";
import { useScoreConfigs } from "./hooks";
import { allowedAnswers, kindLabel } from "./scores";
import type { ScoreConfig } from "./types";

function ScoreRow({ config: c }: { config: ScoreConfig }) {
  return (
    <tr>
      <td>
        <span className="flex flex-col gap-0.5">
          <span className="text-sm font-semibold">{c.name}</span>
          {c.description && <span className="text-xs text-muted">{c.description}</span>}
        </span>
      </td>
      <td className="text-muted">{kindLabel(c)}</td>
      <td className="max-w-[320px] text-muted">{allowedAnswers(c)}</td>
      <td className="text-muted">
        {c.created_by} · {formatRelative(c.created_at)}
      </td>
      <td className="pin-end">
        <div className="flex justify-end">
          <ArchiveMenu kind="scoreConfig" row={c} />
        </div>
      </td>
    </tr>
  );
}

export function ScoresTab({ newButton }: { newButton: ReactNode }) {
  const tabs = useArchiveTabs("scoreConfig", useScoreConfigs());
  const configs = tabs.query;
  const archived = tabs.counts.archived ?? 0;
  return (
    <>
      <ArchiveTabs kind="scoreConfig" {...tabs.counts} />
      <Box>
        <QueryState query={configs} what="scores">
          {configs.data?.length ? (
            <div className="overflow-x-auto">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Score</th>
                    <th>Type</th>
                    <th>Answers</th>
                    <th>Created</th>
                    <th className="pin-end" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {configs.data.map((c) => (
                    <ScoreRow key={c.id} config={c} />
                  ))}
                </tbody>
              </table>
            </div>
          ) : tabs.view === "archived" || archived > 0 ? (
            <ArchiveEmpty kind="scoreConfig" view={tabs.view} archived={archived} create={newButton} />
          ) : (
            <EmptyState title="No scores yet" action={newButton}>
              A score is one thing people judge by hand — pass / fail, a 1–5 scale or a few categories. Every
              queue picks the scores it asks, so the same score means the same thing everywhere.
            </EmptyState>
          )}
        </QueryState>
      </Box>
    </>
  );
}
